import { Injectable } from '@angular/core';

import { environment } from '@environments/environment';
import Resources from '@constants/resourses';
import { IQuotesTariff } from '../models/quotesTariff.interface';

import { SessionService } from './session.service';
import { UtilityService } from './utility.service';

type TealiumEventType = 'view' | 'link';

@Injectable()
export class TealiumUtagService {
  // Persistent data layer (standard + page view variables). Link event variables are never stored here.
  tealiumData: any;
  scriptSrc = '';
  private isScriptLoading = false;
  private eventQueue: { type: TealiumEventType, payload: any }[] = [];

  constructor(
    private sessionService: SessionService,
    private utilityService: UtilityService
  ) {
    // "noview" flag (no first page automatic view event) for Single Page Apps (SPAs)
    (window as any).utag_cfg_ovrd = { ...((window as any).utag_cfg_ovrd || {}), noview: true };

    // Standard variables
    this.tealiumData = {
      app_name: environment.tealium_app_name,
      app_environment: environment.tealium_app_environment,
      app_platform_release_timestamp: environment.tealium_app_platform_release,
      campaign_maskcode: this.getCampaignMaskcode()
    };
    // utag.js writes its own variables into utag_data, so it gets a copy to keep our data layer clean
    (window as any).utag_data = { ...this.tealiumData };
  }

  // Generic script loader with callbacks
  getScript(src: string, onLoad: () => void, onError: () => void) {
    const s = document.createElement('script');
    s.type = 'text/javascript';
    s.async = true;
    s.src = src;
    s.addEventListener('load', onLoad, false);
    s.addEventListener('error', onError, false);

    const t = document.getElementsByTagName('script')[0];
    t.parentNode.insertBefore(s, t);
  }

  // Config settings used to build the path to the utag.js file
  setConfig(config: { account: string, profile: string, environment: string }) {
    if (config.account !== undefined && config.profile !== undefined && config.environment !== undefined) {
      this.scriptSrc = 'https://tags.tiqcdn.com/utag/' + config.account + '/' + config.profile + '/' + config.environment + '/utag.js';
    }
  }

  /**
   * @description Tracks a page view. Page variables are persisted for the rest of the journey.
   * @param data - Page variables (page_name, page_type, customer_id...)
   */
  view(data: any = {}) {
    this.updateDataLayer(data);
    this.track('view', { ...this.tealiumData, tealium_event: 'view' });

    // start_quote is emitted once per session, after the first page view of whichever page the customer enters on
    if (!this.sessionService.getItemFromSession(Resources.SessionStorageKeys.IsTealiumStartQuote)) {
      this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.IsTealiumStartQuote, true);
      this.link({ tealium_event: 'start_quote' });
    }
  }

  /**
   * @description Tracks a link event. Event variables are only sent with this event and do not persist.
   * @param data - Event variables, including tealium_event (start_quote, click_through...)
   */
  link(data: any = {}) {
    this.updateDataLayer();
    this.track('link', { ...this.tealiumData, ...this.removeEmptyValues(data) });
  }

  /**
   * @description SHA256 hash of the lowered and trimmed email, only when the email passes validation
   * @param email - Customer email
   */
  hashEmail(email?: string): string | undefined {
    const normalisedEmail = email?.toString().toLowerCase().trim();
    if (!normalisedEmail || !Resources.Regex.Email.test(normalisedEmail)) {
      return undefined;
    }
    return this.utilityService.encryptIntoSha256(normalisedEmail);
  }

  /**
   * @description Base64-encoded version of the customer's full email address (UTF-8 safe)
   * @param email - Customer email
   */
  encodeEmail(email?: string): string | undefined {
    const trimmedEmail = email?.toString().trim();
    if (!trimmedEmail) {
      return undefined;
    }
    const bytes = new TextEncoder().encode(trimmedEmail);
    return btoa(Array.from(bytes, (byte) => String.fromCharCode(byte)).join(''));
  }

  /**
   * @description Customer's full postcode in standard format, e.g. "CF62 7JE"
   */
  getFullPostcode(): string | undefined {
    const postcode = this.sessionService.getItemFromLocal(Resources.SessionStorageKeys.Postcode)?.toString().replace(/\s/g, '').toUpperCase();
    if (!postcode) {
      return undefined;
    }
    return postcode.length > 3 ? `${postcode.slice(0, -3)} ${postcode.slice(-3)}` : postcode;
  }

  /**
   * @description Contract type of the tariff, e.g. "Fixed for 13 Months" or "Variable"
   * @param quote - Selected tariff
   */
  getProductDetail(quote?: IQuotesTariff): string | undefined {
    if (!quote?.TarrifType) {
      return undefined;
    }
    return quote.TarrifType + ((quote.ContractLength > 0) ? ` for ${quote.ContractLength} Months` : '');
  }

  // Merge page variables and the dynamic standard variables into the persistent data layer
  private updateDataLayer(data: any = {}) {
    const partialPostcode = this.utilityService.getPartialPostcode();
    const isLoggedIn = !!this.utilityService.isLoggedIn();
    this.tealiumData = {
      ...this.tealiumData,
      ...this.removeEmptyValues(data),
      login_status: isLoggedIn ? 'Logged In' : 'Not Logged In',
      // APD pre-fill = details prefilled from the My CtM account. Previous quote pre-fill is not offered in this journey.
      prefill_preference: (isLoggedIn && this.utilityService.hasPrefilledUserDetails()) ? 'APD pre-fill' : 'No pre-fill',
      ...(partialPostcode ? { partial_postcode: partialPostcode } : {})
    };
    (window as any).utag_data = { ...this.tealiumData };
  }

  // Send the event, loading utag.js once and queueing events until it is ready
  private track(type: TealiumEventType, payload: any) {
    if (this.scriptSrc === '') {
      return;
    }

    const utag = (window as any).utag;
    if (typeof utag?.view === 'function' && typeof utag?.link === 'function') {
      utag[type](payload);
      return;
    }

    this.eventQueue.push({ type, payload });
    if (this.isScriptLoading) {
      return;
    }

    this.isScriptLoading = true;
    this.getScript(this.scriptSrc, () => {
      this.isScriptLoading = false;
      const queuedEvents = this.eventQueue;
      this.eventQueue = [];
      queuedEvents.forEach((event) => (window as any).utag?.[event.type](event.payload));
    }, () => {
      this.isScriptLoading = false;
      this.eventQueue = [];
    });
  }

  // AFFCLIE query parameter overrides the default maskcode and persists for the session
  private getCampaignMaskcode(): string {
    const affclie = new URLSearchParams(window.location.search).get('AFFCLIE');
    if (affclie) {
      this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.TealiumCampaignMaskcode, affclie);
    }
    return this.sessionService.getItemFromSession(Resources.SessionStorageKeys.TealiumCampaignMaskcode)?.toString() || environment.tealium_app_campaign_maskcode;
  }

  // Avoid overwriting persisted values with empty ones
  private removeEmptyValues(data: any = {}): any {
    return Object.keys(data).reduce((result: any, key: string) => {
      if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
        result[key] = data[key];
      }
      return result;
    }, {});
  }
}
